/**
 * Attendance RTK Query Endpoints
 * 
 * Module-level API file — all endpoints inject into the central baseApi.
 * Inherits auth headers + 401 handling automatically.
 * 
 * All endpoints configured for:
 * - Silent background refresh (no UI flicker)
 * - Fresh data on every mount
 */

import { baseApi } from '../../../../store/api/baseApi';

// Response types
interface TotalRegisteredEmpResponse {
  success: boolean;
  totalRegisteredEmp: number;
  totalContractEmpCount: number;
  totalEmployee: number;
  message: string;
}

interface TotalTeacherAttendanceResponse {
  success: boolean;
  totalPresentToday: number;
  totalPresentRegToday: number;
  totalContractualPresentToday: number;
}

interface TotalTeacherAbsenceResponse {
  success: boolean;
  totalAbsentToday: number;
  totalAbsentRegToday: number;
  totalContractualAbsentToday: number;
}

interface TotalPreviousDayAttendanceResponse {
  success: boolean;
  totalPreviousDayAttendance: number;
  totalPreviousDayAttendanceReg: number;
  totalPreviousDayContractualAttendance: number;
}

// Query argument types
interface StaffAttendanceArgs {
  page?: number;
  year?: number;
  month?: number;
  date?: number;
  empType?: 'regular' | 'contractual';
}

interface HistoricalKVAttendanceArgs {
  page?: number;
  fromDate?: string;
  toDate?: string;
  orderBy?: string;
  order?: 'ASC' | 'DESC';
}

interface EmployeeRegisterArgs {
  empId: string | number;
  month: number;
  year: number;
}

interface DateRangeArgs {
  fromDate: string;
  toDate: string;
}

interface TopPerformingSummaryArgs {
  fromDate: string;
  toDate: string;
  flag?: 'kv' | 'ro';
  limit?: number;
  order?: 'ASC' | 'DESC';
}

interface TopPerformingEmployeesArgs {
  fromDate: string;
  toDate: string;
  flag?: 'regular' | 'contractual';
  order?: 'ASC' | 'DESC';
  limit?: number;
}

interface DailyAttendanceNoPagingArgs {
  year?: number;
  month?: number;
  date?: number;
  kvid?: number;
}


export const attendanceRtkApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    // Total registered employees
    getTotalEmployees: build.query<TotalRegisteredEmpResponse | null, void>({
      query: () => '/pis/totalregisteredemp',
      transformResponse: (response: any) => {
        if (response?.success) {
          return {
            success: response.success,
            totalRegisteredEmp: response.totalRegisteredEmp,
            totalContractEmpCount: response.totalContractEmpCount,
            totalEmployee: response.totalEmployee,
            message: response.message,
          };
        }
        return null;
      },
      providesTags: ['Employee'],
    }),

    // Total teacher attendance (detailed version with breakdown)
    getTeacherAttendanceDetails: build.query<TotalTeacherAttendanceResponse | null, void>({
      query: () => '/pis/totalteacherattendance',
      transformResponse: (response: any) => {
        if (response?.success) {
          return {
            success: response.success,
            totalPresentToday: response.totalPresentToday,
            totalPresentRegToday: response.totalPresentRegToday,
            totalContractualPresentToday: response.totalContractualPresentToday,
          };
        }
        return null;
      },
      providesTags: ['Attendance'],
    }),

    // Staff present today
    getStaffPresentToday: build.query<TotalTeacherAttendanceResponse | null, void>({
      query: () => '/pis/presenttoday',
      transformResponse: (response: any) => {
        if (response?.success) {
          return {
            success: response.success,
            totalPresentToday: response.totalPresentToday,
            totalPresentRegToday: response.totalPresentRegToday,
            totalContractualPresentToday: response.totalContractualPresentToday,
          };
        }
        return null;
      },
      providesTags: ['Attendance'],
    }),

    // Staff absent today
    getStaffAbsentToday: build.query<TotalTeacherAbsenceResponse | null, void>({
      query: () => '/pis/absenttoday',
      transformResponse: (response: any) => {
        if (response?.success) {
          return {
            success: response.success,
            totalAbsentToday: response.absentToday,
            totalAbsentRegToday: response.absentRegToday,
            totalContractualAbsentToday: response.absentConToday,
          };
        }
        return null;
      },
      providesTags: ['Attendance'],
    }),

    // Total previous day attendance
    getTotalPreviousDayAttendance: build.query<TotalPreviousDayAttendanceResponse | null, void>({
      query: () => '/pis/totalpreviousdayattendance',
      transformResponse: (response: any) => {
        if (response?.success) {
          return {
            success: response.success,
            totalPreviousDayAttendance: response.totalPreviousDayAttendance,
            totalPreviousDayAttendanceReg: response.totalRegPreviousDayAttendance,
            totalPreviousDayContractualAttendance: response.totalContractualPreviousDayAttendance,
          };
        }
        return null;
      },
      providesTags: ['Attendance'],
    }),

    // Staff attendance register (paginated)
    getStaffAttendanceRegister: build.query<any, StaffAttendanceArgs>({
      query: ({ page = 1, year, month, date, empType }) => {
        let url = `/pis/employeeattendance?page=${page}`;
        if (year !== undefined) url += `&year=${year}`;
        if (month !== undefined) url += `&month=${String(month).padStart(2, '0')}`;
        if (date !== undefined) url += `&date=${String(date).padStart(2, '0')}`;
        if (empType === 'contractual') url += `&emptype=contractual`;
        else if (empType === 'regular') url += `&emptype=regular`;
        return url;
      },
      transformResponse: (response: any) => response?.success ? response : null,
      providesTags: ['Attendance'],
    }),

    // Employee profile (supports emptype query param for contractual)
    getEmployeeProfile: build.query<any, { empId: string | number; emptype?: string }>({
      query: ({ empId, emptype }) => emptype ? `/pis/profile/${empId}?emptype=${emptype}` : `/pis/profile/${empId}`,
      transformResponse: (response: any) => response?.success ? response.data : null,
      providesTags: ['Employee'],
    }),

    // Employee attendance register
    getEmployeeRegister: build.query<any, EmployeeRegisterArgs>({
      query: ({ empId, month, year }) => `/pis/register?empid=${empId}&month=${month}&year=${year}`,
      transformResponse: (response: any) => response?.success ? response : null,
      providesTags: ['Attendance'],
    }),

    // Historical KV attendance (paginated)
    getHistoricalKVAttendance: build.query<any, HistoricalKVAttendanceArgs>({
      query: ({ page = 1, fromDate, toDate, orderBy, order }) => {
        let url = `/pis/pdailyattendancebykvs?page=${page}`;
        if (fromDate) url += `&fromdate=${fromDate}`;
        if (toDate) url += `&todate=${toDate}`;
        if (orderBy) url += `&orderby=${orderBy}`;
        if (order) url += `&order=${order}`;
        return url;
      },
      transformResponse: (response: any) => response?.success ? response : null,
      providesTags: ['Attendance'],
    }),


    // Student details
    getStudentDetails: build.query<any, string | number>({
      query: (studentId) => `/sis/studentdetails?studentid=${studentId}`,
      transformResponse: (response: any) => {
        if (response?.success && Array.isArray(response.data) && response.data.length > 0) {
          return response.data[0];
        }
        return null;
      },
      providesTags: ['Student'],
    }),

    // Student guardian details
    getStudentGuardianDetails: build.query<any, string | number>({
      query: (studentId) => `/sis/guardiandetails?studentid=${studentId}`,
      transformResponse: (response: any) => response?.success ? response.data : null,
      providesTags: ['Student'],
    }),

    // Student address profile
    getStudentAddressProfile: build.query<any, string | number>({
      query: (studentId) => `/sis/addressprofile?studentid=${studentId}`,
      transformResponse: (response: any) => response?.success ? response.data : null,
      providesTags: ['Student'],
    }),

    // Student first admission details
    getStudentFirstAdmissionDetails: build.query<any, string | number>({
      query: (studentId) => `/sis/firstadmissiondetails?studentid=${studentId}`,
      transformResponse: (response: any) => response?.success ? response.data : null,
      providesTags: ['Student'],
    }),

    // Student present KV details
    getStudentPresentKVDetails: build.query<any, string | number>({
      query: (studentId) => `/sis/presentkvsdetails?studentid=${studentId}`,
      transformResponse: (response: any) => response?.success ? response.data : null,
      providesTags: ['Student'],
    }),

    // Student current academic session details
    getStudentCurrentAcademicDetails: build.query<any, string | number>({
      query: (studentId) => `/sis/currentacademicsessiondetails?studentid=${studentId}`,
      transformResponse: (response: any) => response?.success ? response.data : null,
      providesTags: ['Student'],
    }),

    // Student previous session details
    getStudentPreviousSessionDetails: build.query<any, string | number>({
      query: (studentId) => `/sis/previoussessiondetails?studentid=${studentId}`,
      transformResponse: (response: any) => response?.success ? response.data : null,
      providesTags: ['Student'],
    }),

    // All regions
    getAllRegions: build.query<any[], void>({
      query: () => '/shared/allros',
      transformResponse: (response: any) => {
        if (response?.success && Array.isArray(response.data)) {
          return response.data.filter((region: any) => region.roName !== 'Abroad KV');
        }
        return [];
      },
      providesTags: ['KVS'],
    }),

    // All states
    getAllStates: build.query<any[], void>({
      query: () => '/pis/allstate',
      transformResponse: (response: any) => {
        if (response?.success && Array.isArray(response.data)) {
          return response.data.map((state: any) => ({
            stateId: state.state_id,
            stateName: state.state_name,
            isActive: state.is_active === '1',
            lastUpdated: state.last_updated,
          }));
        }
        return [];
      },
      providesTags: ['KVS'],
    }),

    // RO Summary for regional heatmap and top/bottom regions
    getROSummary: build.query<any, DateRangeArgs>({
      query: ({ fromDate, toDate }) => `/pis/rosummary?fromdate=${fromDate}&todate=${toDate}`,
      transformResponse: (response: any) => response?.success ? response : null,
      providesTags: ['Attendance'],
    }),

    // Top Performing Summary (for charts and trends - KV or RO level)
    getTopPerformingSummary: build.query<any, TopPerformingSummaryArgs>({
      query: ({ fromDate, toDate, flag = 'kv', limit = 15, order = 'DESC' }) =>
        `/pis/toperformingsummary?fromdate=${fromDate}&todate=${toDate}&flag=${flag}&limit=${limit}&order=${order}`,
      transformResponse: (response: any) => response?.success ? response : null,
      providesTags: ['Attendance'],
    }),

    // Top Performing Employees (Most Improved, High Risk, Declining)
    getTopPerformingEmployees: build.query<any, TopPerformingEmployeesArgs>({
      query: ({ fromDate, toDate, flag = 'regular', order = 'DESC', limit = 20 }) =>
        `/pis/topperformingempdaterange?fromdate=${fromDate}&todate=${toDate}&flag=${flag}&order=${order}&limit=${limit}`,
      transformResponse: (response: any) => response?.success ? response : null,
      providesTags: ['Employee'],
    }),

    // Attendance Percentage for Date Range (aggregated metrics)
    getAttendancePercDateRange: build.query<any, DateRangeArgs>({
      query: ({ fromDate, toDate }) =>
        `/pis/attendancepercdaterange?fromdate=${fromDate}&todate=${toDate}`,
      transformResponse: (response: any) => response?.success ? response : null,
      providesTags: ['Attendance'],
    }),

    // Daily Attendance Summary (regular/contractual percentages)
    getDailyAttendanceSummary: build.query<any, DateRangeArgs>({
      query: ({ fromDate, toDate }) =>
        `/pis/pdailyattendance?fromdate=${fromDate}&todate=${toDate}`,
      transformResponse: (response: any) => response?.success ? response : null,
      providesTags: ['Attendance'],
    }),

    // Total KVIS (school count and KV count)
    getTotalKVIS: build.query<any, void>({
      query: () => '/kvis/total',
      transformResponse: (response: any) => response?.success ? response : null,
      providesTags: ['KVS'],
    }),

    // Daily Attendance by KVS (no paging - for overview)
    getDailyAttendanceByKVSNoPaging: build.query<any, DailyAttendanceNoPagingArgs | void>({
      query: (args) => {
        let url = '/pis/dailyattendancebykvsnopaging';
        if (args) {
          const params: string[] = [];
          if (args.year !== undefined) params.push(`year=${args.year}`);
          if (args.month !== undefined) params.push(`month=${String(args.month).padStart(2, '0')}`);
          if (args.date !== undefined) params.push(`date=${String(args.date).padStart(2, '0')}`);
          if (args.kvid !== undefined) params.push(`kvid=${args.kvid}`);
          if (params.length > 0) url += `?${params.join('&')}`;
        }
        return url;
      },
      transformResponse: (response: any) => response?.success ? response : null,
      providesTags: ['Attendance'],
    }),
  }),
  overrideExisting: false,
});

// Export auto-generated hooks
export const {
  useGetTotalEmployeesQuery,
  useGetTeacherAttendanceDetailsQuery,
  useGetStaffPresentTodayQuery,
  useGetStaffAbsentTodayQuery,
  useGetTotalPreviousDayAttendanceQuery,
  useGetStaffAttendanceRegisterQuery,
  useGetEmployeeProfileQuery,
  useGetEmployeeRegisterQuery,
  useGetHistoricalKVAttendanceQuery,
  useGetStudentDetailsQuery,
  useGetStudentGuardianDetailsQuery,
  useGetStudentAddressProfileQuery,
  useGetStudentFirstAdmissionDetailsQuery,
  useGetStudentPresentKVDetailsQuery,
  useGetStudentCurrentAcademicDetailsQuery,
  useGetStudentPreviousSessionDetailsQuery,
  useGetAllRegionsQuery,
  useGetAllStatesQuery,
  // New optimized endpoints
  useGetROSummaryQuery,
  useGetTopPerformingSummaryQuery,
  useGetTopPerformingEmployeesQuery,
  useGetAttendancePercDateRangeQuery,
  useGetDailyAttendanceSummaryQuery,
  useGetTotalKVISQuery,
  useGetDailyAttendanceByKVSNoPagingQuery,
} = attendanceRtkApi;
